"use client";

import {
  isNativeAppMarketingPath,
  isOwnAppHost,
} from "@/lib/native-app/native-app-agent";
import { useNativeAppKind } from "@/lib/native-app/native-app-context";
import { setPendingShare } from "@/lib/native-app/pending-share";
import { useAppShortcuts } from "@/lib/native-app/use-app-shortcuts";
import {
  exitApp,
  hideSplashScreen,
  onHardwareBack,
  onShareReceived,
  openExternalUrl,
  type RgbColor,
  setStatusBarForTheme,
  setWebViewBackground,
} from "@v1/native";
import { useTheme } from "next-themes";
import { usePathname, useRouter } from "next/navigation";
import { useEffect } from "react";

/** Auth screens paint their own light card even in dark mode. */
const LIGHT_CHROME_PATHS: readonly string[] = [
  "/login",
  "/forgot-password",
  "/reset-password",
  "/portal-sign-in",
  "/invite",
  "/portal-accept",
];

function isLightChromePath(pathname: string): boolean {
  if (pathname === "/") return true;
  return LIGHT_CHROME_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

const LIGHT_PAGE_RGB: RgbColor = { r: 255, g: 255, b: 255 };

/** Dark theme page colour as painted, so native gaps blend in. */
function darkPageRgb(): RgbColor {
  const match = /rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(
    window.getComputedStyle(document.body).backgroundColor,
  );
  if (!match) return { r: 26, g: 27, b: 31 };
  return { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]) };
}

/**
 * Store-app behaviour the web never needs: splash hand-off, Android back key,
 * status bar colour, icon quick actions, foreign pages kept out of the WebView.
 * Renders nothing; inert in browsers.
 */
export function NativeAppShell(): null {
  const kind = useNativeAppKind();
  const router = useRouter();
  const pathname = usePathname();
  const { resolvedTheme, forcedTheme } = useTheme();
  useAppShortcuts(kind);

  useEffect(() => {
    if (!kind) return;
    // After first paint, so the splash never uncovers a blank WebView.
    const frame = requestAnimationFrame(() => {
      void hideSplashScreen();
    });
    return () => cancelAnimationFrame(frame);
  }, [kind]);

  useEffect(() => {
    if (!kind) return;
    let active = true;
    let cleanup: () => void = () => {};
    void onHardwareBack((canGoBack) => {
      if (canGoBack) window.history.back();
      else void exitApp();
    }).then((unsubscribe) => {
      if (active) cleanup = unsubscribe;
      else unsubscribe();
    });
    return () => {
      active = false;
      cleanup();
    };
  }, [kind]);

  useEffect(() => {
    if (!kind) return;
    const theme = forcedTheme ?? resolvedTheme;
    const darkUi = theme === "dark" && !isLightChromePath(pathname ?? "");
    void setStatusBarForTheme(darkUi);
    // Next frame: the theme class is applied, so the body shows its real colour.
    const frame = requestAnimationFrame(() => {
      void setWebViewBackground(darkUi ? darkPageRgb() : LIGHT_PAGE_RGB);
    });
    return () => cancelAnimationFrame(frame);
  }, [kind, forcedTheme, resolvedTheme, pathname]);

  useEffect(() => {
    if (!kind) return;
    let active = true;
    let cleanup: () => void = () => {};
    void onShareReceived((item) => {
      setPendingShare(item);
      // Staff: Ablage upload sheet. Portal: "Neues Anliegen" with the photo.
      router.push(kind === "portal" ? "/portal/tasks/new" : "/drive");
    }).then((unsubscribe) => {
      if (active) cleanup = unsubscribe;
      else unsubscribe();
    });
    return () => {
      active = false;
      cleanup();
    };
  }, [kind, router]);

  useEffect(() => {
    if (!kind) return;
    const onClick = (event: MouseEvent): void => {
      if (event.defaultPrevented || event.button !== 0) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.protocol !== "http:" && url.protocol !== "https:") return;
      const inAppPage =
        isOwnAppHost(kind, url.hostname) &&
        !isNativeAppMarketingPath(url.pathname);
      if (inAppPage) {
        const sameOrigin = url.origin === window.location.origin;
        // Customer-subdomain links and new-tab links stay in this WebView.
        if (!sameOrigin || anchor.target === "_blank") {
          event.preventDefault();
          router.push(`${url.pathname}${url.search}${url.hash}`);
        }
        return;
      }
      event.preventDefault();
      void openExternalUrl(url.toString());
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [kind, router]);

  return null;
}
